import Button from 'components/Button';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import { ModalType } from 'core/useCases/modal/types';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useModalStore } from 'stores/modal';
import {
    SField,
    SHint,
    SLabel,
    SLlmLink,
    SLlmLinks,
    SPromptBox,
    SPromptText,
    SStepTitle,
    STextInput,
    STextarea,
} from './styles';

type FormInputs = {
    job: string;
    sector: string;
    location: string;
    precision?: string;
};

type Props = {
    closeModal(): void;
};

const generatePrompt = (values: FormInputs): string => {
    const precisionLine = values.precision ? `- Précision sur la recherche : ${values.precision}\n` : '';

    return `Tu es un expert en networking professionnel et en recherche de contacts BtoB. Je cherche à constituer un réseau de contacts professionnels ciblé pour développer ma carrière.

            Contexte de ma recherche :
            - Poste visé / Poste des contacts recherchés : ${values.job}
            - Secteur d'activité : ${values.sector}
            - Localisation : ${values.location}
            ${precisionLine}
            Ta mission : génère une liste de 20 contacts professionnels réalistes que je pourrais contacter pour du networking. Ces contacts doivent être des personnes susceptibles de m'aider dans ma recherche (recruteurs, managers, dirigeants, DRH, etc. dans le secteur concerné).

            Génère UNIQUEMENT un fichier CSV brut (pas de markdown, pas de balises, pas de bloc de code, pas de mise en forme) avec exactement ces colonnes dans cet ordre :
            Gender (m/f),First Name,Last Name,E-mail Address,Mobile Phone,Business Phone,Job Title,Company,Notes

            Règles STRICTES :
            - NE PAS utiliser de markdown : pas de **, pas de __, pas de [], pas de <>
            - Les emails doivent être en texte brut uniquement, exemple : prenom.nom@entreprise.fr (JAMAIS [prenom.nom@entreprise.fr](mailto:prenom.nom@entreprise.fr))
            - Gender : "m" pour homme, "f" pour femme
            - Les numéros de téléphone français au format 06 12 34 56 78 (mobile) ou 01 23 45 67 89 (fixe)
            - Job Title : poste du contact (ex: Directeur RH, Responsable Recrutement, DG, etc.)
            - Company : entreprise du contact dans le secteur ${values.sector} basée à ${values.location}
            - Notes : une courte note sur pourquoi ce contact est pertinent pour mon réseau

            Commence directement par la ligne d'en-tête CSV, sans aucun texte avant ou après dans le fichier.
    `;
};

const PromptGeneratorForm: FC<Props> = ({ closeModal }) => {
    const { t } = useTranslation();
    const openModal = useModalStore((s) => s.openModal);
    const [generatedPrompt, setGeneratedPrompt] = useState<string | null>(null);

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<FormInputs>();

    const onSubmit = handleSubmit((values) => {
        setGeneratedPrompt(generatePrompt(values));
    });

    const handleCopy = () => {
        if (!generatedPrompt) return;
        navigator.clipboard.writeText(generatedPrompt).then(() => {
            toast.success(t('prompt-generator.copy-success'));
        });
    };

    const handleOpenImport = () => {
        openModal({
            defaultValues: { pasteMode: true },
            title: t('contact.import-contacts'),
            type: ModalType.Contacts,
        });
    };

    const handleOpenGemini = () => {
        if (!generatedPrompt) return;
        navigator.clipboard.writeText(generatedPrompt).then(() => {
            toast.info(t('prompt-generator.gemini-hint'));
            window.open('https://gemini.google.com/app', '_blank', 'noopener,noreferrer');
        });
    };

    if (generatedPrompt) {
        const encodedPrompt = encodeURIComponent(generatedPrompt);
        const chatGptUrl = `https://chatgpt.com/?q=${encodedPrompt}`;
        const claudeUrl = `https://claude.ai/new?q=${encodedPrompt}`;
        const grokUrl = `https://x.com/i/grok?text=${encodedPrompt}`;

        return (
            <>
                <SStepTitle>{t('prompt-generator.prompt-ready')}</SStepTitle>
                <SHint>{t('prompt-generator.prompt-hint')}</SHint>
                <SLlmLinks>
                    <Button onClick={handleCopy} variant="outlined">
                        {t('prompt-generator.copy')}
                    </Button>
                    <SLlmLink href={chatGptUrl} rel="noopener noreferrer" target="_blank">
                        {t('prompt-generator.open-chatgpt')}
                    </SLlmLink>
                    <SLlmLink href={claudeUrl} rel="noopener noreferrer" target="_blank">
                        {t('prompt-generator.open-claude')}
                    </SLlmLink>
                    <SLlmLink as="button" onClick={handleOpenGemini}>
                        {t('prompt-generator.open-gemini')}
                    </SLlmLink>
                    <SLlmLink href={grokUrl} rel="noopener noreferrer" target="_blank">
                        {t('prompt-generator.open-grok')}
                    </SLlmLink>
                </SLlmLinks>
                <SPromptBox>
                    <SPromptText>{generatedPrompt}</SPromptText>
                </SPromptBox>
                <BaseModal.BottomActions>
                    <Button onClick={() => setGeneratedPrompt(null)} variant="text">
                        {t('prompt-generator.back')}
                    </Button>
                    <Button onClick={handleOpenImport}>{t('prompt-generator.import-csv')}</Button>
                </BaseModal.BottomActions>
            </>
        );
    }

    return (
        <form onSubmit={onSubmit}>
            <SField>
                <SLabel htmlFor="pg-job">{t('prompt-generator.job')} *</SLabel>
                <STextInput
                    id="pg-job"
                    placeholder={t('prompt-generator.job-placeholder')}
                    {...register('job', { required: true })}
                    hasError={!!errors.job}
                />
            </SField>
            <SField>
                <SLabel htmlFor="pg-sector">{t('prompt-generator.sector')} *</SLabel>
                <STextInput
                    id="pg-sector"
                    placeholder={t('prompt-generator.sector-placeholder')}
                    {...register('sector', { required: true })}
                    hasError={!!errors.sector}
                />
            </SField>
            <SField>
                <SLabel htmlFor="pg-location">{t('prompt-generator.location')} *</SLabel>
                <STextInput
                    id="pg-location"
                    placeholder={t('prompt-generator.location-placeholder')}
                    {...register('location', { required: true })}
                    hasError={!!errors.location}
                />
            </SField>
            <SField>
                <SLabel htmlFor="pg-precision">{t('prompt-generator.precision')}</SLabel>
                <STextarea
                    id="pg-precision"
                    placeholder={t('prompt-generator.precision-placeholder')}
                    rows={3}
                    {...register('precision')}
                />
            </SField>
            <BaseModal.BottomActions>
                <Button onClick={closeModal} variant="text">
                    {t('common.cancel')}
                </Button>
                <Button submit>{t('prompt-generator.generate')}</Button>
            </BaseModal.BottomActions>
        </form>
    );
};

export default PromptGeneratorForm;
